import React, { useContext, useState, useEffect } from 'react';
import { Search, AlertCircle, Loader2, Eye, Filter, X, ChevronDown, Settings, Building2, User, Calendar, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import superAdminServices from '../../../services/superAdminServices';
import Pagination from '../../../components/Pagination';

export default function AllProcesses() {
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [selectedControlElements, setSelectedControlElements] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 25,
    total_pages: 0
  });

  const [filters, setFilters] = useState({
    processName: '',
    organization: '',
    status: '',
    focalPoint: '',
    sortBy: 'newest'
  });

  // Fetch processes on mount and when pagination changes
  useEffect(() => {
    fetchProcesses();
  }, [pagination.page, pagination.page_size]);

  const fetchProcesses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      // Add filters
      if (filters.processName) params.process_name = filters.processName;
      if (filters.organization) params.organization = filters.organization;
      if (filters.status) params.status = filters.status;
      if (filters.focalPoint) params.focal_point = filters.focalPoint;
      if (filters.sortBy) params.ordering = mapSortToOrdering(filters.sortBy);

      const response = await superAdminServices.getAllProcesses(authTokens.access, params);

      setProcesses(response.results || []);
      setPagination(prev => ({
        ...prev,
        count: response.count,
        total_pages: response.total_pages
      }));
    } catch (err) {
      setError(err.message);
      setProcesses([]);
    } finally {
      setLoading(false);
    }
  };

  const mapSortToOrdering = (sortBy) => {
    const sortMap = {
      'process_name': 'process_name',
      'organization': 'organisation__organisation_name',
      'status': 'status',
      'process_date': 'process_date',
      'focal_point': 'focal_point',
      'newest': '-created_at',
      'oldest': 'created_at'
    };
    return sortMap[sortBy] || '-created_at';
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    fetchProcesses();
  };

  const handleClearFilters = () => {
    setFilters({
      processName: '',
      organization: '',
      status: '',
      focalPoint: '',
      sortBy: 'newest'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchProcesses(), 0);
  };

  const handleShowFilters = () => {
    setShowFiltersSection(!showFiltersSection);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1
    }));
  };

  const handleViewProcess = (processId) => {
    navigate(`/admin-panel/processes/${processId}`);
  };

  const handleShowControlElements = (event, elements) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX
    });
    setSelectedControlElements(elements);
  };

  const handleClosePopup = () => {
    setSelectedControlElements(null);
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedControlElements && !event.target.closest('.control-popup') && !event.target.closest('.control-trigger')) {
        handleClosePopup();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedControlElements]);

  const getStatusBadge = (status) => {
    const statusColors = {
      'draft': 'bg-gray-100 text-gray-700',
      'published': 'bg-green-100 text-green-700',
      'achieved': 'bg-blue-100 text-blue-700',
      'archived': 'bg-red-100 text-red-700'
    };

    const colorClass = statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
        {status === 'draft' ? 'Brouillon' : 
         status === 'published' ? 'Publié' : 
         status === 'achieved' ? 'Réalisé' : 
         status === 'archived' ? 'Archivé' : status}
      </span>
    );
  };

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Tous les processus</h2>
          <p className="text-xs text-gray-500 mt-1">Gérez tous les processus du système</p>
        </div>
        
        <button
          onClick={handleShowFilters}
          className="inline-flex items-center px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filtres
          <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Process Name Search */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nom du processus
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un processus..."
                  value={filters.processName}
                  onChange={(e) => handleFilterChange('processName', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Organization */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Organisation
              </label>
              <input
                type="text"
                placeholder="Nom de l'organisation"
                value={filters.organization}
                onChange={(e) => handleFilterChange('organization', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
                <option value="achieved">Réalisé</option>
                <option value="archived">Archivé</option>
              </select>
            </div>

            {/* Focal Point */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Point focal
              </label>
              <input
                type="text"
                placeholder="Point focal"
                value={filters.focalPoint}
                onChange={(e) => handleFilterChange('focalPoint', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Sort */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Trier par
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
                <option value="process_name">Nom du processus (A-Z)</option>
                <option value="organization">Organisation (A-Z)</option>
                <option value="status">Statut</option>
                <option value="process_date">Date du processus</option>
                <option value="focal_point">Point focal</option>
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-end space-x-3 mt-4">
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="h-4 w-4 mr-1" />
              Effacer
            </button>
            <button
              onClick={handleApplyFilters}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Control Elements Popup */}
      {selectedControlElements && (
        <div
          className="control-popup fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <FileText className="h-4 w-4 mr-2 text-blue-600" />
              Éléments de contrôle
            </h3>
            <button
              onClick={handleClosePopup}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {selectedControlElements.map((element, index) => (
              <div
                key={element.id}
                className="bg-gray-50 rounded-lg p-3 border border-gray-100"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900 mb-1">
                      {element.element_name}
                    </p>
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar className="h-3 w-3 mr-1" />
                      {new Date(element.control_date).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    {index + 1}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Processes Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Processus</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Element de controle</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Organisation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Point focal</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date processus</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Activités</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Tâches</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Créé par</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                      <span className="ml-2 text-sm text-gray-600">Chargement...</span>
                    </div>
                  </td>
                </tr>
              ) : processes.length > 0 ? (
                processes.map((process) => (
                  <tr
                    key={process.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gradient-to-r from-black to-gray-900 rounded-full flex items-center justify-center mr-3">
                          <Settings className="h-4 w-4 text-green-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-900 mb-1">{process.process_name}</p>

                        </div>
                      </div>
                    </td>
                    <td>
                      {process.element_to_controle && process.element_to_controle.length > 0 && (
                          <div>
                            <div className="flex items-center">
                                <div className="flex items-center  px-2 py-1">
                                  <FileText className="h-3 w-3 text-blue-600 mr-1" />
                                  <span className="text-xs">
                                    {process.element_to_controle[0].element_name}
                                  </span>

                                </div>
                                {process.element_to_controle.length > 1 && (
                                  <button
                                    onClick={(e) => handleShowControlElements(e, process.element_to_controle)}
                                    className="control-trigger ml-2 inline-flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-medium hover:bg-blue-700 transition-colors"
                                    title={`${process.element_to_controle.length - 1} autre(s) élément(s)`}
                                  >
                                    +{process.element_to_controle.length - 1}
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-blue-500 ml-1">
                                    {process.element_to_controle[0].control_date}
                              </p>
                          </div>
                        )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Building2 className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-900">
                          {process.organization?.name || 'Aucune organisation'}
                        </span>
                        {process.organization?.is_subscribed && (
                          <span className="ml-2 inline-block w-2 h-2 bg-green-400 rounded-full" title="Abonné"></span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(process.status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-900">
                        {process.focal_point || 'Non défini'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-600">
                          {new Date(process.process_date).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {process.activities_count} activité(s)
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {process.tasks_count} tâche(s)
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <User className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-900">
                          {process.created_by?.name || process.created_by?.email || 'Inconnu'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleViewProcess(process.id)}
                        className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <Settings className="h-12 w-12 text-gray-300 mb-3" />
                      <p className="text-xs text-gray-500">Aucun processus trouvé</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="processus"
        />
      </div>
    </div>
  );
}
